import { useState, useEffect } from 'react';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { useTutorAgreements } from '@/hooks/useTutorAgreements';
import { initializeTutorTerms } from '@/utils/initializeTutorTerms';
import { UnifiedConsentModal } from './UnifiedConsentModal';

interface UnifiedConsentGuardProps {
  children: React.ReactNode;
}

export interface PendingAgreement {
  type: 'universal_terms' | 'tutor_terms';
  version: string;
  title: string;
  content: string;
  effectiveDate: string;
}

export const UnifiedConsentGuard = ({ children }: UnifiedConsentGuardProps) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { checkTutorAgreement } = useTutorAgreements();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [pendingAgreements, setPendingAgreements] = useState<PendingAgreement[]>([]);

  useEffect(() => {
    // Only check agreements when auth is loaded and user exists
    if (!isAuthLoading && user?.id && user?.role) {
      checkAllUserAgreements();
    }
  }, [user?.id, user?.role, isAuthLoading]);

  const checkAllUserAgreements = async () => {
    if (!user?.id || !user?.role) return;

    setIsChecking(true);
    
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.warn('UnifiedConsentGuard: Agreement check timed out, proceeding anyway');
      setIsChecking(false);
    }, 5000);

    try {
      // Initialize terms if they don't exist
      await initializeTutorTerms();
      
      const pending: PendingAgreement[] = [];
      const agreementTypes = new Set<string>(); // Track agreement types to avoid duplicates

      // Check universal terms (for all users)
      const hasUniversalAgreement = await checkUserUniversalAgreement(user.id);
      if (!hasUniversalAgreement) {
        const universalTerms = await getCurrentTermsByType('universal_terms');
        if (universalTerms && !agreementTypes.has('universal_terms')) {
          pending.push({
            type: 'universal_terms',
            version: universalTerms.version,
            title: universalTerms.title,
            content: universalTerms.content,
            effectiveDate: universalTerms.effective_date
          });
          agreementTypes.add('universal_terms');
          console.log('UnifiedConsentGuard: Added universal terms version', universalTerms.version);
        }
      }

      // Check tutor-specific terms (only for tutors)
      if (user.role === 'tutor') {
        const hasTutorAgreement = await checkTutorAgreement(user.id);
        if (!hasTutorAgreement) {
          const tutorTerms = await getCurrentTermsByType('tutor_terms');
          if (tutorTerms && !agreementTypes.has('tutor_terms')) {
            pending.push({
              type: 'tutor_terms',
              version: tutorTerms.version,
              title: tutorTerms.title,
              content: tutorTerms.content,
              effectiveDate: tutorTerms.effective_date
            });
            agreementTypes.add('tutor_terms');
            console.log('UnifiedConsentGuard: Added tutor terms version', tutorTerms.version);
          }
        }
      }

      console.log('UnifiedConsentGuard: Pending agreements:', pending);
      
      if (pending.length > 0) {
        setPendingAgreements(pending);
        setShowConsentModal(true);
      }
    } catch (error) {
      console.error('UnifiedConsentGuard: Error checking agreements:', error);
      // Don't block on error - let users through
    } finally {
      clearTimeout(timeoutId);
      setIsChecking(false);
    }
  };

  const checkUserUniversalAgreement = async (userId: string): Promise<boolean> => {
    try {
      // Get current universal terms version
      const currentTerms = await pb.collection('terms_versions')
        .getFirstListItem('is_current = true && agreement_type = "universal_terms"')
        .catch(() => null);

      if (!currentTerms) {
        return true; // If no universal terms exist, don't block access
      }

      // Check if user has agreed to the current version
      const agreement = await pb.collection('user_agreements')
        .getFirstListItem(`user_id = "${userId}" && terms_version = "${currentTerms.version}" && agreement_type = "universal_terms" && is_active = true`)
        .catch(() => null);

      return !!agreement;
    } catch (error) {
      console.error('UnifiedConsentGuard: Error checking universal agreement:', error);
      return true; // Don't block on error
    }
  };

  const getCurrentTermsByType = async (agreementType: string) => {
    try {
      // Get the current version of the specified agreement type
      const currentTerms = await pb.collection('terms_versions')
        .getFirstListItem(`is_current = true && agreement_type = "${agreementType}"`);
      
      console.log(`UnifiedConsentGuard: Found current ${agreementType} terms:`, currentTerms);
      return currentTerms;
    } catch (error) {
      console.error(`Error fetching current ${agreementType} terms:`, error);
      
      // Fallback: get the latest version by created date if no current version is set
      try {
        console.log(`UnifiedConsentGuard: No current ${agreementType} found, getting latest version`);
        const latestTerms = await pb.collection('terms_versions')
          .getFirstListItem(`agreement_type = "${agreementType}"`, {
            sort: '-created'
          });
        console.log(`UnifiedConsentGuard: Found latest ${agreementType} terms:`, latestTerms);
        return latestTerms;
      } catch (fallbackError) {
        console.error(`Error fetching latest ${agreementType} terms:`, fallbackError);
        return null;
      }
    }
  };

  const handleConsentAccepted = () => {
    console.log('UnifiedConsentGuard: All consents accepted');
    setShowConsentModal(false);
    setPendingAgreements([]);
  };

  const handleConsentCancelled = () => {
    console.log('UnifiedConsentGuard: Consent cancelled');
    setShowConsentModal(false);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      {children}
      {showConsentModal && user && pendingAgreements.length > 0 && (
        <UnifiedConsentModal
          open={showConsentModal}
          onConsent={handleConsentAccepted}
          onCancel={handleConsentCancelled}
          userId={user.id}
          userType={user.role as 'tutor' | 'student' | 'admin'}
          pendingAgreements={pendingAgreements}
        />
      )}
    </>
  );
};